
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserCircle, LogOut } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface UserProfileProps {
  isMobile?: boolean;
}

export const UserProfile = ({ isMobile = false }: UserProfileProps) => {
  const { toast } = useToast();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getUserInfo = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error fetching user session:", error);
          toast({
            title: "Error",
            description: "Could not fetch user information",
            variant: "destructive",
          });
          return;
        }
        
        if (data.session) {
          const userId = data.session.user.id;
          setUserEmail(data.session.user.email);
          
          // Fetch admin profile if it exists
          const { data: profileData } = await supabase
            .from("admin_profiles")
            .select("display_name, avatar_url")
            .eq("id", userId)
            .maybeSingle();
            
          if (profileData) {
            setDisplayName(profileData.display_name);
            setAvatarUrl(profileData.avatar_url);
          }
        }
      } catch (err) {
        console.error("Error in getUserInfo:", err);
      } finally {
        setIsLoading(false);
      }
    };

    getUserInfo();
  }, [toast]);

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();

    if (error) {
      toast({
        title: "Error signing out",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Signed out",
      description: "You have been signed out successfully",
    });
  };

  const getInitials = () => {
    if (displayName) {
      return displayName
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    
    if (!userEmail) return "?";
    return userEmail.split("@")[0].slice(0, 2).toUpperCase();
  };

  if (isLoading || !userEmail) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className={`${isMobile ? "p-0 hover:bg-transparent" : "p-1 hover:bg-blue-800 rounded-md"}`}
        >
          <Avatar className="h-8 w-8">
            {avatarUrl ? (
              <AvatarImage src={avatarUrl} alt="Profile" />
            ) : (
              <AvatarFallback className="bg-slate-100 text-[#1e3a8a] text-xs font-semibold">
                {getInitials()}
              </AvatarFallback>
            )}
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={`bg-white shadow-lg border border-gray-200 ${isMobile ? "w-48" : "w-56"}`}>
        <DropdownMenuItem asChild>
          <Link to="/admin/profile" className="cursor-pointer flex w-full items-center">
            <UserCircle className="mr-2 h-4 w-4" />
            Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600 focus:text-red-600">
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
